import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { user, logout } = useAuth();
  const nav = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const dashLink = user?.role === "admin" ? "/admin" : user?.role === "campus_ambassador" ? "/dashboard/ambassador" : "/dashboard/student";

  return (
    <header className={`fixed top-0 inset-x-0 z-50 transition-all ${scrolled ? "backdrop-blur-xl bg-[#0F172A]/85 border-b border-white/10" : "backdrop-blur-md bg-[#0F172A]/40"}`}>
      <div className="max-w-7xl mx-auto px-5 md:px-8 h-16 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-2">
          <div className="h-10 w-10 rounded-lg bg-white p-1 flex items-center justify-center shadow-md shadow-blue-500/20">
            <img src="/logo.png" alt="Campus Senior" className="h-full w-full object-contain" />
          </div>
          <div className="hidden sm:block leading-tight">
            <div className="font-display font-bold tracking-tight"><span className="text-white">CAMPUS</span> <span className="text-[#2563EB]">SENIOR</span></div>
            <div className="text-[10px] text-slate-400 tracking-[0.2em] uppercase">Know Before You Join</div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-7 text-sm text-slate-300">
          <a href="/#how-it-works" data-testid="nav-how" className="hover:text-white transition">How It Works</a>
          <Link to="/colleges" data-testid="nav-colleges" className="hover:text-white transition">Colleges</Link>
          <a href="/#ambassador" data-testid="nav-ambassador" className="hover:text-white transition">Become Ambassador</a>
          <a href="/#offers" data-testid="nav-offers" className="hover:text-white transition">Offers</a>
          <Link to="/contact" data-testid="nav-contact" className="hover:text-white transition">Contact</Link>
          <Link to="/leaderboard" data-testid="nav-leaderboard" className="hover:text-white transition">Leaderboard</Link>
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          {user ? (
            <>
              <Link to={dashLink} data-testid="nav-dashboard" className="px-4 py-2 rounded-full text-sm border border-white/15 text-white hover:bg-white/5 transition">Dashboard</Link>
              <button data-testid="nav-logout" onClick={logout} className="px-4 py-2 rounded-full text-sm bg-white/5 text-white hover:bg-white/10 border border-white/10 transition">Logout</button>
            </>
          ) : (
            <>
              <Link to="/auth?mode=login" data-testid="nav-login" className="px-4 py-2 rounded-full text-sm border border-white/15 text-white hover:bg-white/5 transition">Login</Link>
              <Link to="/auth?mode=register" data-testid="nav-get-started" className="px-5 py-2 rounded-full text-sm bg-[#F59E0B] hover:bg-amber-600 text-[#0F172A] font-semibold transition glow-amber">Get Started Free</Link>
            </>
          )}
        </div>

        <button data-testid="nav-mobile-toggle" onClick={() => setOpen(!open)} className="lg:hidden text-white p-2">
          {open ? <X size={24}/> : <Menu size={24}/>}
        </button>
      </div>

      {open && (
        <motion.div initial={{opacity:0, y:-10}} animate={{opacity:1, y:0}} className="lg:hidden bg-[#0F172A]/95 border-t border-white/10 px-5 py-6 space-y-4">
          <a href="/#how-it-works" onClick={() => setOpen(false)} className="block text-slate-200">How It Works</a>
          <Link to="/colleges" onClick={() => setOpen(false)} className="block text-slate-200">Colleges</Link>
          <a href="/#ambassador" onClick={() => setOpen(false)} className="block text-slate-200">Become Ambassador</a>
          <a href="/#offers" onClick={() => setOpen(false)} className="block text-slate-200">Offers</a>
          <Link to="/contact" onClick={() => setOpen(false)} className="block text-slate-200">Contact</Link>
          <Link to="/leaderboard" onClick={() => setOpen(false)} className="block text-slate-200">Leaderboard</Link>
          <div className="pt-3 flex gap-3 border-t border-white/10">
            {user ? (
              <>
                <Link to={dashLink} onClick={() => setOpen(false)} className="flex-1 text-center px-4 py-2 rounded-full border border-white/15">Dashboard</Link>
                <button onClick={() => { logout(); setOpen(false); }} className="flex-1 px-4 py-2 rounded-full bg-white/5">Logout</button>
              </>
            ) : (
              <>
                <Link to="/auth?mode=login" onClick={() => setOpen(false)} className="flex-1 text-center px-4 py-2 rounded-full border border-white/15">Login</Link>
                <Link to="/auth?mode=register" onClick={() => setOpen(false)} className="flex-1 text-center px-4 py-2 rounded-full bg-[#F59E0B] text-[#0F172A] font-semibold">Get Started</Link>
              </>
            )}
          </div>
        </motion.div>
      )}
    </header>
  );
}
